import { Link, useSearchParams } from "react-router-dom";
import { ShieldAlert, RotateCcw, LifeBuoy } from "lucide-react";

/* Human-friendly recovery screen for SocialPulse SSO failures.
   The backend 302s here with ?stage=<code>&ref=<id> whenever any step of the
   OIDC exchange fails — token endpoint, userinfo, invalid state, etc. The
   traveller sees a readable message + the reference id to quote in support. */
const STAGE_COPY = {
  authorize_error:          { title: "Sign-in was cancelled",            help: "Looks like you cancelled sign-in on SocialPulse. Give it another try." },
  missing_code_or_state:    { title: "The sign-in link looks incomplete", help: "The link back from SocialPulse was missing some information. Try signing in again." },
  invalid_state:            { title: "That sign-in attempt timed out",   help: "Sign-in attempts expire for your safety. Please start again." },
  token_endpoint_unreachable:{title: "We couldn't reach SocialPulse",     help: "SocialPulse didn't answer. Check your connection and try again — the service may be briefly down." },
  token_exchange_failed:    { title: "SocialPulse rejected the sign-in", help: "SocialPulse couldn't complete the hand-off. If this keeps happening, send us the reference below." },
  userinfo_unreachable:     { title: "We couldn't fetch your profile",   help: "SocialPulse signed you in but we couldn't load your profile. Please try once more." },
  userinfo_failed:          { title: "SocialPulse profile unavailable",  help: "SocialPulse returned an error when we asked for your profile. Try again in a minute." },
  userinfo_missing_sub:     { title: "Your SocialPulse profile is incomplete", help: "SocialPulse didn't include a user id with your profile. Please sign out of SocialPulse and try again." },
  missing_access_token:     { title: "No sign-in token returned",        help: "SocialPulse accepted the sign-in but didn't send a token. Please try again." },
  auth_backend_not_ready:   { title: "Our sign-in service is restarting", help: "Give it 30 seconds and try again — this is temporary." },
};

export default function SsoError() {
  const [params] = useSearchParams();
  const stage = params.get("stage") || "unknown";
  const ref = params.get("ref") || "";
  const copy = STAGE_COPY[stage] || { title: "SocialPulse sign-in didn't finish",
    help: "Something unexpected happened during sign-in. Please try again, and if it keeps failing, send us the reference below." };

  return (
    <div className="min-h-[70vh] grid place-items-center p-5" data-testid="sso-error-page">
      <div className="max-w-md w-full border-2 border-rose-200 bg-rose-50/40 rounded-3xl p-6 sm:p-8 text-center">
        <div className="w-12 h-12 mx-auto rounded-2xl bg-rose-100 text-rose-700 grid place-items-center">
          <ShieldAlert className="w-6 h-6" />
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-rose-700 mt-3">SocialPulse sign-in</div>
        <h1 className="font-serif font-800 text-2xl text-slate-900 mt-1">{copy.title}</h1>
        <p className="text-sm text-slate-600 mt-3">{copy.help}</p>

        {ref && (
          <div className="mt-5 inline-flex items-center gap-2 bg-white border border-rose-200 rounded-full px-3 py-1.5 text-xs font-mono text-slate-700" data-testid="sso-error-ref">
            Reference: <b data-testid="sso-error-ref-value">{ref}</b>
          </div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <Link to="/login" replace data-testid="sso-error-retry"
            className="inline-flex items-center justify-center gap-1.5 bg-emerald-600 text-white rounded-full px-5 py-2.5 font-700 hover:bg-emerald-500">
            <RotateCcw className="w-4 h-4" /> Try again
          </Link>
          <Link to="/support" data-testid="sso-error-support"
            className="inline-flex items-center justify-center gap-1.5 border border-slate-300 text-slate-700 rounded-full px-5 py-2.5 font-700 hover:border-emerald-500 hover:text-emerald-600">
            <LifeBuoy className="w-4 h-4" /> Contact support
          </Link>
        </div>

        <p className="text-[11px] text-slate-500 mt-5">
          You can also continue exploring without an account — your travel information stays available for discovery pages.
        </p>
      </div>
    </div>
  );
}
